/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';

const SENSOR_PREFIX = 'SENSOR_';

/**
 * @removeSensorPrefix
 * 
 * Provides a utility to remove the "Sensor_" prefix from a sensor label.
 * 
 * ```typescript
 * 
 * label = "Sensor_Temperature"
 * removeSensorPrefix(label) // returns "Temperature"
 * 
 * ```
 * 
 * @param  {string} sensorLabel The sensor label to be modified.
 */
export const removeSensorPrefix = (sensorLabel: string) => {
  const prefix = SENSOR_PREFIX;
  if (sensorLabel.startsWith(prefix)) {
    return sensorLabel.substring(prefix.length);
  }
  return sensorLabel;
};

/**
 * @groupSensorData
 * 
 * Provides a utility to group sensor data by sensor accumulator index.
 * 
 * ```typescript
 * 
 * const avlEvents = [{
 *    avlEvent: {
 *      eventTime: "2021-09-30T18:00:00.000Z",
 *      deviceDataConverted: {
 *        accumulators: [{
 *          label: "Sensor_Temperature",
 *          type: "Temperature",
 *          units: "C",
 *          value: 20.5
 *        }]
 *      }
 *   }
 * }]
 * 
 * groupSensorData(avlEvents) // returns [{
 *   id: "temperature",
 *   data: [{
 *   value: 20.5,
 *   units: "C",
 *   y: 20.5,
 *   x: "2021-09-30T18:00:00.000Z",
 *   label: "Temperature",
 *   type: "Temperature",
 *   eventTime: "2021-09-30T18:00:00.000Z"
 * }]
 * 
 * ```
 * 
 * @param  {object[]} avlEvents The AVL events to be grouped.
 */
export const groupSensorData = (avlEvents) => {
  const sensorData = {};
  if (!isNil(avlEvents) && !isEmpty(avlEvents)) {
    avlEvents?.forEach((event) => {
      const {eventTime, deviceDataConverted} = event?.avlEvent || {};
      if (deviceDataConverted?.accumulators) {
        deviceDataConverted.accumulators?.forEach((accumulator) => {
          const {label, index: accumulatorIndex} = accumulator;
          if (label.startsWith(SENSOR_PREFIX)) {
            const id = removeSensorPrefix(label)?.toLowerCase();
            if (!sensorData[accumulatorIndex]) {
              sensorData[accumulatorIndex] = {
                id,
                data      : [],
                threshold : {max: 0, min: 0}
              };
            }
            sensorData[accumulatorIndex].data.push({
              label : removeSensorPrefix(label),
              value : accumulator.value,
              units : accumulator.units,
              type  : accumulator.type,
              y     : accumulator.value,
              x     : eventTime,
              eventTime
            });
          }
        });
      }
    });
    return Object.values(sensorData);
  }
  return null;
};
